const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

/**
 * Initializes avatar upload fields with drag-and-drop support
 * @param {HTMLElement} parent - Parent element to search within
 */
export function init(parent = document) {
    parent.querySelectorAll('.avatar-upload-widget').forEach((widget) => {
        if (widget.dataset.initialized === 'true') return;
        widget.dataset.initialized = 'true';

        const elements = getAvatarElements(widget);
        if (!elements.fileInput || !elements.previewContainer) return;

        attachAvatarEvents(elements);
    });
}

/**
 * Retrieves all necessary DOM elements for an avatar field
 */
function getAvatarElements(widget) {
    const fileInput = widget.querySelector('input[type="file"]');
    const fieldName = fileInput?.name;

    return {
        fileInput,
        previewContainer: widget.querySelector('.avatar-preview-container'),
        preview: widget.querySelector(`#avatar-preview-${fieldName}`),
        deleteCheckbox: widget.querySelector('input[type="checkbox"]')
    };
}

/**
 * Attaches all event listeners to avatar elements
 */
function attachAvatarEvents(elements) {
    const {fileInput, previewContainer, preview, deleteCheckbox} = elements;

    // File input change
    fileInput?.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
            if (validateFile(file)) {
                displayAvatarPreview(file, preview);
            } else {
                fileInput.value = '';
            }
        }
    });

    // Drag and drop
    setupAvatarDragAndDrop(previewContainer, fileInput, preview);

    // Delete checkbox
    deleteCheckbox?.addEventListener('change', () => {
        if (deleteCheckbox.checked) {
            // Show deleted state
            preview.style.opacity = '0.3';
            preview.style.filter = 'grayscale(100%)';
        } else {
            // Restore
            preview.style.opacity = '1';
            preview.style.filter = 'none';
        }
    });
}

/**
 * Validates file size and type
 */
function validateFile(file) {
    if (file.size > MAX_FILE_SIZE) {
        alert('Le fichier est trop volumineux. Taille maximale : 5 Mo');
        return false;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
        alert('Format non supporté. Formats acceptés : JPG, PNG, GIF');
        return false;
    }

    return true;
}

/**
 * Displays avatar preview
 */
function displayAvatarPreview(file, preview) {
    const reader = new FileReader();
    reader.onload = (event) => {
        preview.src = event.target.result;
        preview.style.opacity = '1';
        preview.style.filter = 'none';
    };
    reader.readAsDataURL(file);
}

/**
 * Sets up drag-and-drop functionality for avatar
 */
function setupAvatarDragAndDrop(container, fileInput, preview) {
    container.addEventListener('dragover', (event) => {
        event.preventDefault();
        applyDragEffect(container, preview);
    });

    container.addEventListener('dragleave', (event) => {
        event.preventDefault();
        removeDragEffect(container, preview);
    });

    container.addEventListener('drop', (event) => {
        event.preventDefault();
        removeDragEffect(container, preview);

        const file = event.dataTransfer.files[0];
        if (!file || !file.type.startsWith('image/')) return;

        if (validateFile(file)) {
            updateFileInput(fileInput, file);
            displayAvatarPreview(file, preview);
        }
    });
}

/**
 * Applies visual effects during drag over
 */
function applyDragEffect(container, preview) {
    // Container effect
    container.style.borderColor = '#ef4444';
    container.style.backgroundColor = 'rgba(127, 29, 29, 0.3)';
    container.style.transform = 'scale(1.05)';

    // Preview effect
    preview.style.transform = 'scale(1.1)';
    preview.style.filter = 'brightness(1.3)';
    preview.style.boxShadow = '0 0 30px rgba(239, 68, 68, 0.8), 0 0 60px rgba(239, 68, 68, 0.4)';
}

/**
 * Removes visual effects after drag
 */
function removeDragEffect(container, preview) {
    // Container effect
    container.style.borderColor = '';
    container.style.backgroundColor = '';
    container.style.transform = '';

    // Preview effect
    preview.style.transform = '';
    preview.style.filter = '';
    preview.style.boxShadow = '';
}

/**
 * Updates file input with dropped file
 */
function updateFileInput(fileInput, file) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    fileInput.files = dataTransfer.files;
}
